import type {
  ClinicFormData,
  ClinicCreatedData,
  CreateUserRequest,
  UserCreatedData,
  Specialization,
  SubscriptionPlan,
  TokenizeCardRequest,
  TokenizeCardResponse,
  SubscribeRequest,
  SubscribeResponse,
} from '../types/signup';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:443/api/v1';

/**
 * Fetch all available specializations
 * GET /api/v1/specializations
 */
export const fetchSpecializations = async (): Promise<Specialization[]> => {
  const response = await fetch(`${API_BASE_URL}/specializations`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch specializations');
  }

  const result = await response.json();
  return result.data || [];
};

/**
 * Create a new clinic
 * POST /api/v1/signup/clinics
 */
export const createClinic = async (data: ClinicFormData): Promise<ClinicCreatedData> => {
  const response = await fetch(`${API_BASE_URL}/signup/clinics`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create clinic');
  }

  const result = await response.json();
  return result.data ?? result;
};

/**
 * Create a new user
 * POST /api/v1/signup/users
 * Returns 409 Conflict if email already exists
 */
export const createUser = async (data: CreateUserRequest): Promise<UserCreatedData> => {
  const response = await fetch(`${API_BASE_URL}/signup/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    
    // Handle duplicate email error
    if (response.status === 409) {
      throw new Error('Este correo electrónico ya está registrado');
    }
    
    throw new Error(errorData.message || 'Failed to create user');
  }

  return response.json();
};

// ── Billing helpers ───────────────────────────────────────────────────────────

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/**
 * Fetch active subscription plans
 * GET /api/v1/billing/plans
 */
export const fetchPlans = async (): Promise<SubscriptionPlan[]> => {
  const response = await fetch(`${API_BASE_URL}/billing/plans`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch subscription plans');
  }

  const result = await response.json();
  return result.data || [];
};

/**
 * Tokenise the user's card — raw data is proxied to n1co, never stored.
 * POST /api/v1/billing/tokenize
 */
export const tokenizeCard = async (
  data: TokenizeCardRequest
): Promise<TokenizeCardResponse> => {
  const response = await fetch(`${API_BASE_URL}/billing/tokenize`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const result: TokenizeCardResponse = await response.json();

  if (!response.ok || !result.success) {
    throw new Error(result.error || 'Failed to tokenize card');
  }

  return result;
};

/**
 * Create / retry subscription.
 * POST /api/v1/billing/subscribe
 */
export const subscribe = async (
  data: SubscribeRequest
): Promise<SubscribeResponse> => {
  const response = await fetch(`${API_BASE_URL}/billing/subscribe`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  const result: SubscribeResponse = await response.json();
  return result;
};
